// Analytics utility component and functions for the SCN website
import React, { useEffect } from 'react';

// Configuration
const MEASUREMENT_ID = 'G-06VQLFCLCB';

// Track page views
export const trackPageView = (path) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href
    });
    console.log('Analytics: Page view tracked for', path);
  }
};

// Track custom events
export const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
    console.log('Analytics: Event tracked', eventName, params);
  }
};

// Analytics Provider component
export const AnalyticsProvider = ({ children }) => {
  useEffect(() => {
    // Initialize page view tracking
    const handleRouteChange = (url) => {
      trackPageView(url);
    };

    // Track initial page view
    trackPageView(window.location.pathname);

    // Add listener for route changes if using a router
    if (typeof window !== 'undefined') {
      const unlisten = window.history.listen && window.history.listen(({ location }) => {
        handleRouteChange(location.pathname);
      });

      // Clean up listener
      return () => {
        if (unlisten) unlisten();
      };
    }
  }, []);

  return <>{children}</>;
};

// Consent Manager Component
export const AnalyticsConsentBanner = ({ onAccept, onDecline }) => {
  // Check if consent already given
  const [showBanner, setShowBanner] = React.useState(false);
  
  useEffect(() => {
    // Only show banner if consent hasn't been given yet
    const hasConsent = localStorage.getItem('analytics-consent') === 'true';
    setShowBanner(!hasConsent);
    
    // Initialize analytics with the current consent state
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': hasConsent ? 'granted' : 'denied'
      });
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem('analytics-consent', 'true');
    
    if (typeof window !== 'undefined' && window.gtag) {
      // Update consent state in Google Analytics
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    
    setShowBanner(false);
    if (onAccept) onAccept();
  };
  
  const handleDecline = () => {
    localStorage.setItem('analytics-consent', 'false');
    
    if (typeof window !== 'undefined' && window.gtag) {
      // Update consent state in Google Analytics
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    
    setShowBanner(false);
    if (onDecline) onDecline();
  };
  
  if (!showBanner) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-md p-4 z-50 border-t border-gray-200">
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between">
        <div className="mb-4 md:mb-0">
          <p className="text-gray-700">
            We use analytics cookies to understand how you use our website and to improve your experience.
          </p>
        </div>
        <div className="flex space-x-4">
          <button 
            onClick={handleDecline}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
          >
            Decline
          </button>
          <button 
            onClick={handleAccept}
            className="px-4 py-2 bg-green-600 rounded text-white hover:bg-green-700"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default {
  trackPageView,
  trackEvent,
  AnalyticsProvider,
  AnalyticsConsentBanner
};
